import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { fileIntoProject, UnconfirmedDocumentRegistrationError } from "./fileIntoProject";
import type { TemplateFormDef } from "../constants/templateForms";
import type { DocumentTemplateId } from "../constants/documentTemplates";
import {
  attachmentsValue,
  createAttachment,
  documentNameFor,
  type TemplateFormAttachment,
  type TemplateFormValues,
} from "../utils/templateForm";
import { renderTemplateFormDocument } from "../utils/renderTemplateFormDocument";
import type { PdfLocale } from "../utils/templateFormPdf";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";

/** Where in the save a failure happened — the toast names the step. */
export type TemplateFormSaveStep = "number" | "reserve" | "attachments" | "pdf" | "file" | "record";

export class TemplateFormSaveError extends Error {
  constructor(
    readonly step: TemplateFormSaveStep,
    message: string,
    /** Shown to the user alongside the step's copy, when the step's copy alone
     *  would be misleading advice. `reserve` sets it: "another document took
     *  this number, try again" is wrong when the real answer is that the
     *  caller has no permission on the project. Backend messages are English
     *  only, which is why they are an addition and not the whole description. */
    readonly detail?: string,
    readonly cause?: unknown
  ) {
    super(message);
    this.name = "TemplateFormSaveError";
  }
}

export interface TemplateFormSaveInput {
  templateId: DocumentTemplateId;
  form: TemplateFormDef;
  copy: TemplateFormTranslations;
  /** The language the document itself prints in, chosen on the form. */
  pdfLocale: PdfLocale;
  values: TemplateFormValues;
  documentName: string;
  /** True while the name is still the derived `<slug>-nr-<n>` — the reserved
   *  number then rewrites it, so the filename never contradicts the number the
   *  form actually got (the on-form seed is a client-side prediction, computed
   *  against the wrong scope entirely when the page had no project). A name
   *  the user typed is never touched. */
  nameFollowsNumber: boolean;
  attachmentsInPdf: boolean;
  signDigitally: boolean;
  organisation: string;
  orgId: string;
  buildingId: number;
  projectId: number;
  folderId: number | null;
  userId: number;
}

export interface TemplateFormSaveResult {
  /** The filed PDF — what the signing flow and the documents list take. */
  documentId: number;
  filename: string;
  size: number;
  documentNumber: number;
  formId: number;
  /** Attachments the PDF could only name — the caller reports them rather than
   *  letting the "add attachments to the PDF" tick box imply otherwise. */
  notAppended: string[];
}

/** `documents` rows and their storage objects share this bucket. */
/**
 * `next_template_form_number` is a plain `max()+1` with no advisory lock, so N
 * savers racing for the same template in the same project serialise through the
 * unique index one at a time — the Nth needs N attempts. A crew filing their
 * diaries at the end of a shift is exactly that burst, and each attempt is one
 * cheap insert with nothing uploaded yet, so the budget is generous.
 */
const NUMBER_ATTEMPTS = 8;
/**
 * Backing off spreads a burst out instead of having every loser re-collide on
 * the same next number; the jitter is what stops them re-colliding in step.
 *
 * Capped, and low: the whole budget has to stay well inside the time someone
 * will sit watching a Save spinner, so eight attempts cost under a second and a
 * half in total rather than compounding to ten seconds.
 */
const RETRY_BASE_MS = 40;
const RETRY_MAX_MS = 250;
const RETRY_JITTER_MS = 60;
const UNIQUE_VIOLATION = "23505";

function retryDelayMs(attempt: number): number {
  return Math.min(RETRY_BASE_MS * 2 ** attempt, RETRY_MAX_MS) + Math.random() * RETRY_JITTER_MS;
}

/**
 * The document a save under this name would file a new version of, if any.
 *
 * `create_document_with_versioning` identifies a document by (project, folder,
 * parent, filename) — a matching name is not rejected as a duplicate, it
 * versions the existing lineage and demotes what was there off
 * `is_latest_version`, so the original stops being the row anyone sees. That is
 * right when re-filing a corrected act and destructive by accident otherwise,
 * so the caller asks first.
 *
 * The predicate mirrors the RPC's exactly, `is_latest_version` included — which
 * is to say excluded: the RPC does not filter on it, and matching on it here
 * would miss a lineage whose head is elsewhere and report "no collision" just
 * before causing one.
 *
 * It is a warning, not a guard, and cannot be otherwise: this runs as
 * `authenticated` under `documents_select`, while the RPC is SECURITY DEFINER.
 * A document the user cannot see — one under a folder on their deny-list — goes
 * unreported here and is still versioned by the RPC. Closing that would mean
 * asking the RPC itself, which is a different change; what this covers is the
 * ordinary case, where the document about to be demoted is one they can see.
 */
export async function findCollidingDocument(target: {
  projectId: number;
  folderId: number | null;
  filename: string;
}): Promise<{ id: number; filename: string } | null> {
  let query = supabase
    .from("documents")
    .select("id, filename")
    .eq("project_id", target.projectId)
    .eq("filename", target.filename)
    .eq("is_deleted", false)
    .is("parent_document_id", null)
    // The RPC takes the highest version as the lineage it versions from.
    .order("version", { ascending: false })
    .limit(1);
  query =
    target.folderId === null ? query.is("folder_id", null) : query.eq("folder_id", target.folderId);

  const { data, error } = await query.maybeSingle();
  // Never block the save on this: it is a warning, and a transient failure here
  // must not be the reason a filled statutory form cannot be filed.
  if (error) {
    console.error("template form: could not check for a name collision —", error.message);
    return null;
  }
  return data ?? null;
}

/**
 * Uploads one file and files it as a document row, returning its id. Shared by
 * the generated PDF and by every attachment staged from the user's device, so
 * both arrive in the project the same way an ordinary upload would.
 *
 * `onCreated` fires after the RPC confirms a row with a valid action and id,
 * before the backup mirror. The caller needs that fact rather than a return
 * value because mirroring can fail after the document is committed, and a
 * confirmed document must keep its reserved form number.
 */
/**
 * A template form's PDF or attachment, filed into the project. The upload
 * sequence itself is shared — see `fileIntoProject`.
 */
async function fileAsDocument(
  file: File,
  input: TemplateFormSaveInput,
  source: "template_form" | "direct_upload",
  onCreated?: () => void
): Promise<{ id: number; filename: string; size: number }> {
  return fileIntoProject({
    file,
    source,
    onCreated,
    keyPrefix: "forms",
    orgId: input.orgId,
    buildingId: input.buildingId,
    projectId: input.projectId,
    folderId: input.folderId,
    userId: input.userId,
  });
}

/**
 * Claims a document number by inserting the form record. The unique index on
 * (project_id, template_id, document_number) is what makes the number mean
 * something, so a number lost to a concurrent save shows up here — as 23505 on
 * a cheap insert, before anything has been uploaded.
 *
 * Only 23505 is a lost race. Everything else — the RPC refusing a caller with
 * no `UPLOAD_DOCUMENTS` on the project, an RLS rejection, a dropped connection
 * — fails as `reserve`, because telling someone to "try saving again" when
 * they will never be allowed to is advice that never comes true.
 */
async function reserveForm(
  input: TemplateFormSaveInput,
  serialisableValues: Json
): Promise<{ formId: number; documentNumber: number; documentName: string }> {
  let lastError = "";
  for (let attempt = 0; attempt < NUMBER_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs(attempt)));
    }
    const { data: next, error: numberError } = await supabase.rpc("next_template_form_number", {
      p_project_id: input.projectId,
      p_template_id: input.templateId,
    });
    if (numberError) {
      throw new TemplateFormSaveError("reserve", numberError.message, numberError.message);
    }

    // An untyped name follows the number — this number, the reserved one, not
    // the client-side seed the form displayed, which can lag a concurrent save
    // or (at building scope) be computed against the wrong project entirely.
    const documentName = input.nameFollowsNumber
      ? documentNameFor(input.form.nameSlug[input.pdfLocale], next, input.pdfLocale)
      : input.documentName;

    const { data, error } = await supabase
      .from("document_forms")
      .insert({
        // org_id and building_id are set authoritatively by a trigger from the
        // project; these satisfy the client types and are overwritten.
        org_id: input.orgId,
        building_id: input.buildingId,
        project_id: input.projectId,
        folder_id: input.folderId,
        template_id: input.templateId,
        document_number: next,
        document_name: documentName,
        values: serialisableValues,
        attachments_in_pdf: input.attachmentsInPdf,
        sign_digitally: input.signDigitally,
        created_by: input.userId,
      })
      .select("id, document_number")
      .single();

    if (!error) return { formId: data.id, documentNumber: data.document_number, documentName };
    if (error.code !== UNIQUE_VIOLATION) {
      throw new TemplateFormSaveError("reserve", error.message, error.message);
    }
    lastError = error.message;
  }
  // Every attempt lost the same race — this one really is "someone took it".
  throw new TemplateFormSaveError("number", lastError);
}

/** Staged `File`s cannot be stored in jsonb — the row keeps the document each
 *  one became, so a reopened form points at real documents. */
function withoutFiles(values: TemplateFormValues): Json {
  const plain: Record<string, unknown> = {};
  for (const [id, value] of Object.entries(values)) {
    plain[id] = Array.isArray(value)
      ? value.map((entry) =>
          entry && typeof entry === "object" && "file" in entry
            ? { ...(entry as TemplateFormAttachment), file: undefined }
            : entry
        )
      : value;
  }
  return plain as Json;
}

/** Uploads every staged attachment and rewrites it to the document it became. */
async function fileAttachments(input: TemplateFormSaveInput): Promise<TemplateFormValues> {
  const values: TemplateFormValues = { ...input.values };
  for (const section of input.form.sections) {
    for (const field of section.fields) {
      if (field.type !== "attachments") continue;
      const filed: TemplateFormAttachment[] = [];
      for (const attachment of attachmentsValue(values, field.id)) {
        if (attachment.source !== "upload" || !attachment.file) {
          filed.push(attachment);
          continue;
        }
        const doc = await fileAsDocument(attachment.file, input, "direct_upload");
        filed.push(createAttachment(attachment.name, "project", String(doc.id)));
      }
      values[field.id] = filed;
    }
  }
  return values;
}

/**
 * Saves a filled template form: the answers, the attachments, and the PDF the
 * form prints as — all filed into the project the form was opened from.
 *
 * The number is claimed first, before anything is uploaded, so losing a race
 * costs an insert rather than orphaning a stored document. If a later step
 * fails before the numbered PDF could be registered, the reservation is
 * released. An unconfirmed PDF registration retains it until reconciled.
 */
export async function saveTemplateForm(
  input: TemplateFormSaveInput
): Promise<TemplateFormSaveResult> {
  const reservation = await reserveForm(input, withoutFiles(input.values));
  // The number stays reserved once the numbered PDF is confirmed or its
  // registration may have committed. A failure before that releases it.
  let numberMayBeSpent = false;

  try {
    // Rendered before the attachments are uploaded, while the staged files are
    // still in hand — otherwise every one of them would be downloaded straight
    // back out of the storage bucket it had just been put into.
    const rendered = await catching("pdf", () =>
      renderTemplateFormDocument({
        form: input.form,
        templateId: input.templateId,
        locale: input.pdfLocale,
        values: input.values,
        documentNumber: String(reservation.documentNumber),
        organisation: input.organisation,
        attachmentsInPdf: input.attachmentsInPdf,
        signDigitally: input.signDigitally,
      })
    );

    const values = await catching("attachments", () => fileAttachments(input));

    const pdf = new File([rendered.bytes as BlobPart], `${reservation.documentName}.pdf`, {
      type: "application/pdf",
    });
    const filed = await catching("file", () =>
      fileAsDocument(pdf, input, "template_form", () => {
        numberMayBeSpent = true;
      })
    );

    await catching("record", async () => {
      const { error } = await supabase
        .from("document_forms")
        .update({ document_id: filed.id, values: withoutFiles(values) })
        .eq("id", reservation.formId);
      if (error) throw new Error(error.message);
    });

    return {
      documentId: filed.id,
      filename: filed.filename,
      size: filed.size,
      documentNumber: reservation.documentNumber,
      formId: reservation.formId,
      notAppended: rendered.notAppended,
    };
  } catch (error) {
    if (
      error instanceof TemplateFormSaveError &&
      error.step === "file" &&
      error.cause instanceof UnconfirmedDocumentRegistrationError
    ) {
      numberMayBeSpent = true;
    }
    await releaseReservation(reservation.formId, numberMayBeSpent);
    throw error;
  }
}

/**
 * Hands the document number back, so a failed save leaves no gap the next one
 * would skip over.
 *
 * Only while no document has been confirmed to carry it. Once registration
 * confirms a row, releasing would free number N with a PDF printing "Akti nr: N"
 * already in the project — and the retry, reserving N again, would file a
 * second act under the same statutory number, which is precisely what the
 * unique index exists to prevent. Everything from that point on keeps its
 * reservation: the number stays spoken for by the document actually carrying
 * it, and the toast says what did not finish, which is the truth.
 */
async function releaseReservation(formId: number, numberMayBeSpent: boolean): Promise<void> {
  if (numberMayBeSpent) return;

  const { error } = await supabase.from("document_forms").delete().eq("id", formId);
  // Not thrown — the caller is already failing for a better reason, and this is
  // tidy-up. But it must not vanish: `next_template_form_number` is `max()+1`,
  // so a reservation that survives claims its number for good and every future
  // act in the project skips it. A zero-row delete looks identical to success
  // over PostgREST (RLS filtered it, say), which is why the log says "may".
  if (error) {
    console.error(
      `template form: reservation ${formId} may still hold its document number —`,
      error.message
    );
  }
}

async function catching<T>(step: TemplateFormSaveStep, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    throw new TemplateFormSaveError(
      step,
      error instanceof Error ? error.message : String(error),
      error instanceof UnconfirmedDocumentRegistrationError ? error.message : undefined,
      error
    );
  }
}
